import { SessionStorage } from '@utilities/storage'
import Events from '@utilities/events'
import { tns } from 'tiny-slider/src/tiny-slider';

const SESSION_STORAGE_ITEM_NAME = 'last-product-pid'
const SPEC_SHEET_WRAPPER = '.fl_spec_sheet'
const SPEC_SHEET_BUTTON_DOWNLOAD = '[js-hook-spec-sheet-download]'
const SEE_FULL_GALLERY = '.js-fl-see-gallery'
const SPEC_SHEET_BUTTON_DOWNLOAD_WRP = '.fl-download__link'
const SIDEBAR = '.js-fl-sidebar-wrp'
const SIDEBAR_INNER = '.fl-sidebar-wrp__inner'

/**
 * ProductDetail component
 */
class ProductDetail {
  constructor(element) {
    this.element = element
    this.spectSheetWrp = this.element.querySelector(SPEC_SHEET_WRAPPER)
    this.spectSheetButton = this.element.querySelector(SPEC_SHEET_BUTTON_DOWNLOAD)
    this.seeFullGallery = this.element.querySelector(SEE_FULL_GALLERY)
    this.spectSheetButtonWrp = this.element.querySelector(SPEC_SHEET_BUTTON_DOWNLOAD).closest(SPEC_SHEET_BUTTON_DOWNLOAD_WRP)
    this.sidebar = this.element.querySelector(SIDEBAR)
    this.sidebarInner = this.element.querySelector(SIDEBAR_INNER)

    const { pid } = this.element.dataset || {}
    SessionStorage.set(SESSION_STORAGE_ITEM_NAME, pid)

    this.bindEvents()
  }

  bindEvents() {
    window.addEventListener('load', () => {
      this.loadTechSpecificationsSheet()
      Events.$on('spec-sheet-download::reinit', () => {
        this.spectSheetButton = this.element.querySelector(SPEC_SHEET_BUTTON_DOWNLOAD);
        this.spectSheetButtonWrp = this.element.querySelector(SPEC_SHEET_BUTTON_DOWNLOAD).closest(SPEC_SHEET_BUTTON_DOWNLOAD_WRP);
        this.loadTechSpecificationsSheet();
      });
      this.setSidebarHeight();
      this.initializePdpSlider();
    })
    window.addEventListener('resize', () => {
      this.setSidebarHeight();
    });

    if (this.seeFullGallery) {
      this.seeFullGallery.addEventListener('click', () => {
        const firstImage = document.querySelector('[js-hook-image-zoom-full-screen-button]');
        if (firstImage) {
          firstImage.click();
        }
      })
    }

    Events.$on('product::specsheet', () => this.loadTechSpecificationsSheet())
  }

  async featchTeachSpecs(API_KEY, productIds, locale, URL) {
    // check dom elements for stylesheet
    const buttonTpl = this.element.querySelector(SPEC_SHEET_BUTTON_DOWNLOAD);
    const buttonWrp = this.spectSheetButtonWrp;
    const sectionWrp = this.spectSheetWrp;
    if (!buttonTpl || !buttonWrp || !sectionWrp) {
      return [];
    }

    // hide all stylesheet elements before fetch
    buttonTpl.style.display = 'none';
    buttonWrp.style.display = 'none';
    sectionWrp.style.display = 'none';

    // cleanup old clones
    [...buttonWrp.querySelectorAll('[data-specsheet-clone="true"]')].forEach((n) => n.remove());

    const requests = productIds.map(async (productId) => {
      try {
        const res = await fetch(URL, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${API_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: `query {topicProduct(id: "${productId}", locale: "${locale}") {techSpec}}`,
            variables: { preview: true }
          }),
        });

        const json = await res.json();
        return json?.data?.topicProduct?.techSpec ?? null;
      } catch (err) {
        console.error(`Error on ${productId} when load stylesheet:`, err);
        return null;
      }
    });

    const results = await Promise.all(requests);

    // check if there is at least one valid files
    const isUsableUrl = (v) => typeof v === 'string' && v.trim() !== '' && v.trim().toLowerCase() !== 'null';
    const validSpecs = results.filter(isUsableUrl);
    if (validSpecs.length === 0) {
      return [];
    }

    // show style sheet
    const firstHref = validSpecs[0];
    buttonTpl.setAttribute('href', firstHref);
    buttonTpl.setAttribute('target', '_blank');
    buttonTpl.setAttribute('rel', 'noopener noreferrer');
    buttonTpl.style.display = 'block';

    for (let i = 1; i < validSpecs.length; i++) {
      const cloned = buttonTpl.cloneNode(true);
      cloned.setAttribute('href', validSpecs[i]);
      cloned.setAttribute('data-specsheet-clone', 'true');
      cloned.style.display = 'block';
      buttonWrp.appendChild(cloned);
    }

    // show section
    buttonWrp.style.display = '';
    sectionWrp.style.display = '';

    return validSpecs;
  }

  initializePdpSlider() {
    const galleryElement = document.querySelector('.fl-pdp-slider');

    if (!galleryElement) return;

    const isMobile = window.innerWidth < 1023;

    if (isMobile) {
      if (!galleryElement.classList.contains('tns-slider')) {
        this.instance = tns({
          container: galleryElement,
          items: '1',
          controls: false,
          nav: true,
          controlsContainer: false,
          mode: 'carousel',
          mouseDrag: true,
          autoplay: false,
          autoplayButtonOutput: false,
          loop: false,
          autoHeight: false,
        });
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (this.instance) {
        this.instance.destroy();
      }
    }
  }

  setSidebarHeight() {
    if (document.querySelector(SIDEBAR_INNER) && document.querySelector(SIDEBAR)) {
      const windowHeight = window.innerHeight;
      const sidebarHeight = this.sidebarInner.getBoundingClientRect().height;
      const header = document.querySelector('.c-header');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const rawOffset = this.sidebar.getBoundingClientRect().top;
      const pageOffset = Math.max(headerHeight, rawOffset, 0);
      if (windowHeight - pageOffset < sidebarHeight) {
        document.documentElement.style.setProperty('--sidebarTop', `calc(100vh - ${sidebarHeight / 10}rem)`);
      } else {
        document.documentElement.style.setProperty('--sidebarTop', `${pageOffset / 10}rem`);
      }
    }
  }

  /**
   * Load the tech specifications sheet for the current product and display the download button if available.
   */
  loadTechSpecificationsSheet() {
    const URL = window.contentfulURL
    const API_KEY = window.contentfulAPIKey

    if (!URL || !API_KEY) {
      this.spectSheetWrp.style.display = 'none'
      return
    }

    const { countryCode, bundledProduct, bundledProductIds } = this.element.dataset
    const locale = countryCode === 'us' ? 'en-US' : 'en'
    let { baseProductId } = this.element.dataset
    const originalPid = baseProductId

    if (bundledProduct && bundledProduct !== 'null') {
      baseProductId = bundledProduct
    }

    let productIds = []

    if (Array.isArray(bundledProductIds)) {
      productIds = bundledProductIds
    } else {
      // NOTE: This is a workaround to convert the string to an array
      const cleanedString = bundledProductIds.replace(/^\[|\]$/g, '')

      const actualArray = cleanedString === '' ? [] : cleanedString.split(',')
      productIds = actualArray
    }

    if (!productIds || productIds.length === 0) {
      productIds = [baseProductId]
    } else {
      productIds.unshift(originalPid)
    }

    this.featchTeachSpecs(API_KEY, productIds, locale, URL)
  }
}

export default ProductDetail
